import {
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, UrlSerializer } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
} from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
  provideNativeDateAdapter,
} from '@angular/material/core';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { NgxSpinnerModule } from 'ngx-spinner';
import {
  MsalModule,
  MsalService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { jwtInterceptor } from './Core/Interceptors/jwt.interceptor';
import {
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from '../environments/environment';
import { MSALGuardConfigFactory } from '../export-file';
import { MY_DATE_FORMATS } from './Shared/constants/Share/date-formats';
import localeIt from '@angular/common/locales/it';
import localeAr from '@angular/common/locales/ar';
import { registerLocaleData } from '@angular/common';
import { routes } from './app.routes';
import { LanguageUrlSerializerService } from './Core/services/language-url-serializer.service';

registerLocaleData(localeIt);
registerLocaleData(localeAr);

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    provideHttpClient(withInterceptors([jwtInterceptor])),
    provideNativeDateAdapter(),

    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      ToastrModule.forRoot({ positionClass: 'toast-top-right' }),
      NgxSpinnerModule,
      MsalModule.forRoot(
        MSALInstanceFactory(),
        MSALGuardConfigFactory(),
        MSALInterceptorConfigFactory(),
      ),
    ),

    { provide: UrlSerializer, useClass: LanguageUrlSerializerService },

    // MSAL providers
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalBroadcastService,

    // Date providers
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },

    // Locale provider
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const lang = localStorage.getItem('Language') || 'en';
        const localeMap: Record<string, string> = {
          EN: 'en-GB',
          AR: 'ar',
          IT: 'it',
        };
        return localeMap[lang.toUpperCase()] || 'en-GB';
      },
    },
  ],
};
